/**
 * @kgc/rental-pricing — tier-price
 *
 * A bérlés tier-választásának és bruttó alapdíjának EGYETLEN forrása (ADR-037 Am.3 `D-2`).
 *
 * Miért itt: a `rental.service.ts` `private calculateTierPrice`-a volt az egyetlen hely,
 * ahol a cap-ágak és a `Q-T053` lefokozás élt — a preview-endpoint (`rental-calc.ts`) és a
 * FE-varázsló (`wizard-store.ts`) saját, egyszerűsített if-láncot futtatott. Mérve
 * (2026-08-21): 27 napnál a preview `210 000`, a checkout `150 000` (**+40%**), éjféles,
 * egész napos bemeneten — tehát nem naptár-, hanem cap-eredetű eltérés.
 *
 * A csomag I/O-mentes és függőség nélküli (`package.json` `dependencies: {}`), ezért a FE is
 * hívhatja — a `rental-core` NestJS-t húz, onnan a FE nem tud importálni.
 *
 * ⚠️ A logika a kiemeléskor **változatlan**. A viselkedés-változást a hívók átállítása hozza,
 * nem ez a fájl — ezért mérhető külön a két lépés.
 */

/**
 * A tier értékkészlete. NEM `enum`, mert a `PricingTier` enum a `@kgc/rental-core`-ban él,
 * és a függőség iránya `rental-core → rental-pricing` ⇒ ide nem importálható vissza.
 * A két készlet azonosságát a `tier-price.spec.ts` szinkron-őre méri, nem a jóindulat.
 */
/**
 * QA-90 (P0 MONEY) — a vasárnap/ünnep féldíj (`FK-BER-4`) ABLAKA a pénz-úton.
 *
 * A féldíjas napszám (`billableDays`) eddig CSAK meta volt: ugyanaz a payload kiírta a
 * `2,5`-öt és három napot számlázott (`2 500 Ft` a bérlő kárára, 🎬 dev4-live 2026-08-21/25).
 *
 * ⛔ MIÉRT ABLAK, és miért ITT a határ (🚦-ruling 2026-08-25, ⚙️ mérése):
 *  - `1–5` nap: a `days < 7` ág tiszta `dailyRate × days` ⇒ a csere **ráta-független** és a
 *    `28/30` küszöb-kérdéstől is független. Mérve 4 ráta-arányon: mind `+2 500 Ft` rés.
 *    ⚠️ Az ALSÓ határ `1`, nem `2`: a rés a vasárnap/ünnep JELENLÉTÉN áll, nem a bérlés
 *    hosszán — egy `1` napos vasárnapi bérlés ma `5 000`-et számláz `2 500` helyett
 *    (mérve 2026-08-25). Az első ruling `2`-t mondott egy pontatlan összefoglaló-táblázat
 *    miatt; a kizárás külön feltételt igényelt volna, mért indok nélkül.
 *  - `6` nap: a `days>=6 && hasWeekly` cap miatt a rés **előjelet vált**, és rátafüggő
 *    (`5x`→`−2 500`, `6x` és daily-only→`+2 500`).
 *  - `7–27` nap: a mai út OLCSÓBB (heti tarifa + ADR-048 %). Ott a féldíj „visszaadása"
 *    **elvétel** lenne — 27 napnál `+31 250 Ft` a bérlőnek.
 *  - `28+` nap: a `MONTHLY_THRESHOLD = 28` (billableDays-ág) és a `days >= 30` (árazási ág)
 *    ma szétcsúszik — külön tracker-tétel, 🧭 SSoT-bemenet.
 *
 * ⇒ A `6+` sávok a 🧭 SSoT-design mögött állnak. Aki ezt az ablakot tágítja, előbb MÉRJE
 *   újra mind a négyet — a tágítás két sávban a bérlő kárára fordul.
 */
export const HALF_DAY_WINDOW_MIN_DAYS = 1;
export const HALF_DAY_WINDOW_MAX_DAYS = 5;

/**
 * 🔴 GUARD az ablak BŐVÍTŐJÉNEK — a három fogyasztó NEM egy forrásból veszi a `billableDays`-t.
 *
 * Ma egyeznek, de CSAK azért, mert a `< 7` napos ablakban a heti nap-elengedés `0`:
 *
 * ```text
 * rental-calc.ts:~110   Math.max(0.5, bd.billableDays - wkDiscount)   HETI-ELENGEDETT
 * wizard-store.ts:398   ugyanez a FE sajat buildBreakdownDays-eben    HETI-ELENGEDETT
 * rental.service.ts:418 calculateBillableDaysWithDiscounts(...)       NYERS
 * ```
 *
 * Mérve (2026-08-25, azonos kezdet, `wkDiscount = calendarDays >= 7 ? floor(cal/7) : 0`):
 *
 * ```text
 * n=3,5,6   nyers == heti-elengedett          EGYEZIK
 * n=7       6,5   vs  5,5                     SZETCSUSZIK  (5 000 Ft)
 * n=14      13    vs  11                      SZETCSUSZIK (10 000 Ft)
 * ```
 *
 * ⇒ **A `MAX_DAYS` `7`-re (vagy fölé) emelése önmagában NEM elég**: a create-út nyers értéke és a
 * preview/FE elengedett értéke azonnal más árat adna ⇒ visszagyártanánk az ajánlat-vs-létrejött
 * eltérést (`QA-89`, amit a `#1477` zárt). Aki bővít, ELŐBB egységesítse a három forrást — a
 * bővítés a `QA-90` szempontjából egysoros, a parity szempontjából nem az.
 */

export const PRICING_TIER = {
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
} as const;

export type PricingTierValue = (typeof PRICING_TIER)[keyof typeof PRICING_TIER];

export interface TierPriceResult {
  tier: PricingTierValue;
  grossAmount: number;
  tierExplanation: string;
}

/** G-FLOAT-1: HUF kerekítés — a forintnak nincs törtrésze. */
function roundHUF(amount: number): number {
  return Math.round(amount);
}

/**
 * Tier-választás + bruttó alapdíj.
 *
 * @param days     naptári napok (a hívó a `calculateCalendarDays`-ből adja — `D-1`)
 * @param dailyRate   napi díj (Ft)
 * @param weeklyRate  heti díj (Ft) — `0`/hiányzó ⇒ a heti tier NEM alkalmazható
 * @param monthlyRate havi díj (Ft) — `0`/hiányzó ⇒ a havi tier NEM alkalmazható
 * @param billableDays QA-90: a féldíjas napszám (`calculateBillableDaysWithDiscounts`). Ha meg van
 *                     adva, CSAK a `HALF_DAY_WINDOW_MIN_DAYS..MAX_DAYS` ablakban hat — lásd a
 *                     konstansok fölötti indoklást. Hiánya = a korábbi viselkedés, változatlanul.
 */
export function calculateTierPrice(
  days: number,
  dailyRate: number,
  weeklyRate: number,
  monthlyRate: number,
  billableDays?: number
): TierPriceResult {
  // Q-T053 (döntés-sor 33., P0 MONEY) — TIER-ALKALMAZHATÓSÁGI GUARD.
  // Egy tier CSAK akkor alkalmazható, ha a hozzá tartozó díj POZITÍV. Daily-only gépnél
  // a DB-ből null érkezik → 0 coalesce; a korábbi guard nélküli `Math.min(..., weeklyRate)`
  // és `Math.min(..., monthlyRate)` cap ilyenkor 0-ra húzta a végösszeget ⇒ ≥6 naptól
  // INGYENES volt a bérlés (dev4-live: 5/5 érintett tétel `item_total = 0`).
  // A hiányzó díjú tier nem 0-t ad, hanem LEFOKOZ a legközelebbi árazott tierre.
  const hasWeekly = weeklyRate > 0;
  const hasMonthly = monthlyRate > 0;

  if (days < 7) {
    // QA-90: az ablakon BELÜL a féldíjas napszám a szorzó, azon kívül a naptári nap.
    // A `days` (naptári) marad a tier- és cap-döntés alapja — a törtszámú `billableDays`
    // a `>=6` cap-küszöböt elbillentené (`5,5 < 6`), és a viselkedés némán változna.
    const inHalfDayWindow =
      billableDays !== undefined &&
      days >= HALF_DAY_WINDOW_MIN_DAYS &&
      days <= HALF_DAY_WINDOW_MAX_DAYS;
    const chargedDays = inHalfDayWindow ? billableDays : days;

    const grossAmount = roundHUF(dailyRate * chargedDays); // G-FLOAT-1: HUF rounding
    // Cap: if daily total exceeds weekly rate near the boundary, use weekly rate
    // Q-T053: a cap csak árazott heti díjnál él (0 = nincs heti díj, nem "ingyen hét").
    const cappedAmount = hasWeekly && days >= 6 ? Math.min(grossAmount, weeklyRate) : grossAmount;
    return {
      tier: PRICING_TIER.DAILY,
      grossAmount: cappedAmount,
      tierExplanation: `${chargedDays} nap × ${dailyRate} Ft/nap = ${cappedAmount} Ft`,
    };
  }

  // 30+ days - monthly tier
  // M-100: Fixed 30-day month is a deliberate simplification for rental pricing.
  // This is standard industry practice for equipment rental, not calendar-month billing.
  // Q-T053: a havi ág a HETI ELÉ került — a lefokozás a legközelebbi ÁRAZOTT tierre megy.
  // 30+ napnál havi díj nélkül, de heti díjjal a heti aritmetika a helyes; a tiszta
  // napidíjas fallback ott TÚLSZÁMLÁZNA (37 nap: 185 000 Ft vs. a helyes 135 000 Ft).
  if (days >= 30 && hasMonthly) {
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    const monthAmount = roundHUF(monthlyRate * months); // G-FLOAT-1: HUF rounding

    let remainderAmount = 0;
    let remainderExplanation = '';

    // Q-T053: a maradék heti bontása csak árazott heti díjnál — különben napidíjas.
    if (remainingDays >= 7 && hasWeekly) {
      const weeks = Math.floor(remainingDays / 7);
      const extraDays = remainingDays % 7;
      remainderAmount = roundHUF(weeklyRate * weeks) + roundHUF(dailyRate * extraDays); // G-FLOAT-1: HUF rounding
      remainderExplanation = `${weeks} hét × ${weeklyRate} Ft + ${extraDays} nap × ${dailyRate} Ft`;
    } else if (remainingDays > 0) {
      remainderAmount = roundHUF(dailyRate * remainingDays); // G-FLOAT-1: HUF rounding
      remainderExplanation = `${remainingDays} nap × ${dailyRate} Ft`;
    }

    const total = monthAmount + remainderAmount;

    return {
      tier: PRICING_TIER.MONTHLY,
      grossAmount: total,
      tierExplanation: `${months} hónap × ${monthlyRate} Ft${remainderExplanation ? ` + ${remainderExplanation}` : ''} = ${total} Ft`,
    };
  }

  // Weekly tier — 7+ nap árazott heti díjjal. Q-T053: ide fut a 30+ napos eset is,
  // ha nincs havi díj (lefokozás heti szintre), nem tiszta napidíjra.
  if (hasWeekly) {
    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;
    const weekAmount = roundHUF(weeklyRate * weeks); // G-FLOAT-1: HUF rounding
    const dayAmount = roundHUF(dailyRate * remainingDays); // G-FLOAT-1: HUF rounding
    const total = weekAmount + dayAmount;
    // M-016 FIX: Always cap weekly-tier price against monthly rate to prevent
    // price inversion (e.g. 27 days costing more than 30 days monthly rate).
    // Q-T053: a cap csak árazott havi díjnál él.
    const cappedTotal = hasMonthly ? Math.min(total, monthlyRate) : total;

    return {
      tier: PRICING_TIER.WEEKLY,
      grossAmount: cappedTotal,
      tierExplanation: `${weeks} hét × ${weeklyRate} Ft + ${remainingDays} nap × ${dailyRate} Ft = ${cappedTotal} Ft`,
    };
  }

  // Q-T053 FALLBACK — 7+ nap, de a tartamhoz tartozó tier NINCS beárazva.
  // Tiszta napidíjas árazás; a tier-címke DAILY, mert ténylegesen az a díj alkalmazódik.
  // Az M-016 inverzió-védelem megmarad, de csak árazott havi díj mellett.
  const grossAmount = roundHUF(dailyRate * days); // G-FLOAT-1: HUF rounding
  const cappedAmount = hasMonthly ? Math.min(grossAmount, monthlyRate) : grossAmount;

  return {
    tier: PRICING_TIER.DAILY,
    grossAmount: cappedAmount,
    tierExplanation: `${days} nap × ${dailyRate} Ft/nap = ${cappedAmount} Ft (nincs beárazott ${days >= 30 ? 'havi' : 'heti'} díj)`,
  };
}
